import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import {assert} from 'Asserts';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import {
  DatasetViewQuery,
  DatasetViewQueryVariables,
} from 'data-frames/workspace/__generated/DatasetViewQuery';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import DatasetDetails from './DatasetDetails';

interface DatasetViewProps {
  id: string;
}

// eslint doesn't recognize that $limit is used within the PersistedDataFrameGraphQL fragment
/* eslint-disable graphql/template-strings */
export const DATASET_VIEW_QUERY: DocumentNode = gql`
  query DatasetViewQuery($id: StrictString!, $limit: StrictInt!) @skipAudit @engComponent(name: DATA_FRAMES) {
    dataset(id: $id) {
      ... on DatasetNode {
        id
        name
        createdAt
        modifiedAt
        displayId
        originType
        lineageParent {
          ... on ConnectionNode {
            id
          }
          ... on AnalysisNode {
            id
          }
          ... on StudyNode {
            id
          }
          ... on RunNode {
            id
            apiId
          }
          ...InaccessibleInfoGraphQL
        }
        creator {
          id
          name
          handle
          avatarUrl
        }
        folder {
          id
        }
        permissions {
          readable
          writable
          archivable
          unarchivable
        }
        customFields {
          name
          value
        }
        dataFrame {
          ...InaccessibleInfoGraphQL
          ...PersistedDataFrameGraphQL
        }
        ...CoreObjectArchivableGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('CoreObjectArchivable').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('PersistedDataFrame').fragment}
`;
/* eslint-enable graphql/template-strings */

export default function DatasetView({id}: DatasetViewProps): JSX.Element | null {
  assert(BenchlingConfig.ENABLE_ANALYSIS_TOOL, 'ENABLE_ANALYSIS_TOOL is not enabled');
  const result = useBenchlingQuery<DatasetViewQuery, DatasetViewQueryVariables>(DATASET_VIEW_QUERY, {
    variables: {id, limit: DEFAULT_DATA_FRAME_ROW_LIMIT},
  });

  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.SUCCESS) {
    const {data} = result;
    if (isInaccessible(data.dataset)) {
      return (
        <WorkspaceItemInaccessibleGuard
          inaccessibleInfo={data.dataset}
          modelType={ModelTypeConstants.DATASET}
        />
      );
    } else {
      const reload = (): void => {
        result.refetch({id});
      };
      return <DatasetDetails dataset={data.dataset} reload={reload} />;
    }
  } else {
    return null;
  }
}
